import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { retryOperation } from '@/shared/lib/api';
import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { extractModelNameFromFQN } from '@/shared/lib/mlflow';
import { assetKeys, discoveryKeys } from '@/shared/lib/query/queryKeys';
import type { JsonPatchOperation } from '@/shared/types/common';

import { updateModelDescription } from '@/entities/registered-model';

import { type AssetBaseInfo, updateAssetDescription } from '@/features/asset-detail/api';

type UpdateDescriptionParams = {
  entityType: string;
  entityId: string;
  fqn: string;
  newDescription: string;
};

export const useUpdateAssetDescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ entityType, entityId, fqn, newDescription }: UpdateDescriptionParams) => {
      const currentData = queryClient.getQueryData<AssetBaseInfo>(assetKeys.assetBaseInfo(entityType, fqn));
      const oldDescription = currentData?.description || '';

      if (newDescription === oldDescription) {
        return;
      }

      const operations: JsonPatchOperation[] = [
        {
          op: oldDescription ? 'replace' : 'add',
          path: '/description',
          value: newDescription,
        },
      ];

      await updateAssetDescription(entityType, entityId, operations);

      if (entityType === 'mlmodel') {
        const modelName = extractModelNameFromFQN(fqn);

        try {
          await retryOperation(() =>
            updateModelDescription({
              name: modelName,
              description: newDescription,
            })
          );
        } catch (mlflowError) {
          console.error('MLflow description sync failed after retry, rolling back OpenMetadata changes:', mlflowError);

          try {
            const rollbackOperations: JsonPatchOperation[] = [
              oldDescription
                ? { op: 'replace', path: '/description', value: oldDescription }
                : { op: 'remove', path: '/description' },
            ];
            await updateAssetDescription(entityType, entityId, rollbackOperations);
            console.warn('OpenMetadata rollback successful');
          } catch (rollbackError) {
            console.error('Rollback failed:', rollbackError);
          }

          throw new Error('MLflow 설명 동기화에 실패하여 변경사항이 롤백되었습니다.');
        }
      }
    },
    onError: () => {
      notification.error({
        message: NOTIFICATION_MESSAGES.DESCRIPTION_UPDATE_ERROR,
        description: NOTIFICATION_MESSAGES.DESCRIPTION_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: assetKeys.assetBaseInfo(variables.entityType, variables.fqn),
      });
      queryClient.invalidateQueries({
        queryKey: discoveryKeys.searchPrefix(),
      });

      notification.success({
        message: NOTIFICATION_MESSAGES.DESCRIPTION_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DESCRIPTION_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });
};
